import React from 'react';
import { getApiBase } from '../apiBase';

// QuotesPanel — every job (incl. ones captured on a phone call) lands here with
// what was captured: customer, address, and the scope/description. The owner
// sees the phone info right here and sends a price. Jobs still needing a quote
// surface FIRST so nothing waits. Also surfaces jobs/payments created in the
// Aria mobile app (matched by the company's ARIA-XXXX id).

const APP_CO_KEY = 'howto_app_company_id';
const inp = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', marginTop: 8,
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10, color: '#fff', fontSize: 14,
};

export default function QuotesPanel({ ownerId, onClose }) {
  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // ── Jobs/payments from the Aria mobile app (company-scoped) ──
  const [appCompanyId, setAppCompanyId] = React.useState(() => {
    try { return localStorage.getItem(APP_CO_KEY) || ''; } catch (_) { return ''; }
  });
  const [appCoInput, setAppCoInput] = React.useState('');
  const [appJobs, setAppJobs] = React.useState([]);
  const [appSummary, setAppSummary] = React.useState(null);
  const [appLoading, setAppLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!ownerId) { setJobs([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/api/jobs?ownerId=${encodeURIComponent(ownerId)}`);
      const data = await res.json().catch(() => null);
      setJobs(data && data.ok && Array.isArray(data.jobs) ? data.jobs : []);
    } catch { setJobs([]); }
    setLoading(false);
  }, [ownerId]);
  React.useEffect(() => { load(); }, [load]);

  const loadApp = React.useCallback(async () => {
    if (!appCompanyId) { setAppJobs([]); setAppSummary(null); return; }
    setAppLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/api/app/jobs?companyId=${encodeURIComponent(appCompanyId)}`);
      const data = await res.json().catch(() => null);
      setAppJobs(data && data.ok && Array.isArray(data.jobs) ? data.jobs : []);
      setAppSummary(data && data.summary ? data.summary : null);
    } catch { setAppJobs([]); setAppSummary(null); }
    setAppLoading(false);
  }, [appCompanyId]);
  React.useEffect(() => { loadApp(); }, [loadApp]);

  const connectApp = React.useCallback(() => {
    const id = appCoInput.trim().toUpperCase();
    if (!id) return;
    try { localStorage.setItem(APP_CO_KEY, id); } catch (_) {}
    setAppCompanyId(id);
  }, [appCoInput]);

  const disconnectApp = React.useCallback(() => {
    try { localStorage.removeItem(APP_CO_KEY); } catch (_) {}
    setAppCompanyId(''); setAppJobs([]); setAppSummary(null);
  }, []);

  const money = (n) => {
    const num = Number(n);
    return '$' + (Number.isFinite(num) ? Math.round(num) : 0).toLocaleString();
  };
  const hasQuote = (j) => j && (j.quote_low != null || j.quote_high != null);
  const rangeFor = (j) => {
    if (j.quote_low != null && j.quote_high != null) return money(j.quote_low) + '–' + money(j.quote_high);
    if (j.quote_high != null) return money(j.quote_high);
    if (j.quote_low != null) return money(j.quote_low);
    return null;
  };
  const toQuote = jobs.filter((j) => j && !hasQuote(j) && j.status !== 'cancelled' && j.status !== 'paid');
  const quoted = jobs.filter((j) => j && hasQuote(j));

  const safeUrl = (u) => (typeof u === 'string' && /^https?:\/\//i.test(u)) ? u : null;

  const card = (j, i) => (
    <li key={j.id != null ? j.id : `job-${i}`} className="sched-event">
      <div className="sched-event__t">
        {j.customer_name || j.title || 'New job'}
        {j.trade ? <span style={{ fontWeight: 500, color: 'var(--text-2)' }}> · {j.trade}</span> : null}
      </div>
      {j.address && <div className="sched-event__m">{j.address}</div>}
      {j.description && <div style={{ fontSize: 12.5, color: 'var(--text)', marginTop: 4, lineHeight: 1.45 }}>“{j.description}”</div>}
      <div className="sched-event__m" style={{ marginTop: 4 }}>
        {hasQuote(j)
          ? <strong>{rangeFor(j)}</strong>
          : <span style={{ color: 'var(--c-warn,#e6a700)', fontWeight: 600 }}>Needs a price</span>}
        {j.status ? ` · ${j.status}` : ''}
      </div>
    </li>
  );

  const appCard = (j, i) => (
    <li key={j.id != null ? j.id : `appjob-${i}`} className="sched-event">
      <div className="sched-event__t">
        {(j.customer && j.customer.name) || j.description || 'App job'}
        <span style={{ fontWeight: 600, marginLeft: 8, color: j.status === 'paid' ? 'var(--c-good,#3ad29f)' : 'var(--c-warn,#e6a700)' }}>
          {j.status === 'paid' ? 'PAID' : 'Awaiting payment'}
        </span>
      </div>
      {j.description && (j.customer && j.customer.name) && <div style={{ fontSize: 12.5, color: 'var(--text)', marginTop: 4 }}>“{j.description}”</div>}
      <div className="sched-event__m" style={{ marginTop: 4 }}>
        {j.amount ? <strong>{money(j.amount)}</strong> : (j.kind === 'bid' ? 'Estimate' : '')}
        {j.location ? ` · ${j.location}` : ''}
        {j.status !== 'paid' && safeUrl(j.hostedUrl) ? <> · <a href={safeUrl(j.hostedUrl)} target="_blank" rel="noreferrer" style={{ color: '#00d9ff' }}>pay link</a></> : null}
      </div>
    </li>
  );

  return (
    <div className="agent-detail" onClick={onClose}>
      <div className="agent-detail__panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="agent-detail__close" onClick={onClose} aria-label="Close">×</button>
        <div className="agent-detail__title">Quotes &amp; estimates</div>
        <div className="agent-detail__cadence">Per job</div>
        <p className="agent-detail__desc">Every job from a phone call lands here with what was captured — review the details and send a price.</p>

        {loading ? (
          <p className="sched-muted">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="sched-muted">No jobs yet. When a call comes in, the job and what was discussed show up right here, ready to quote.</p>
        ) : (
          <>
            {toQuote.length > 0 && (
              <>
                <div className="agent-detail__section">Ready to quote</div>
                <ul className="sched-events">{toQuote.map(card)}</ul>
              </>
            )}
            {quoted.length > 0 && (
              <>
                <div className="agent-detail__section">Quoted</div>
                <ul className="sched-events">{quoted.map(card)}</ul>
              </>
            )}
          </>
        )}

        {/* ── Jobs & payments from the Aria app ── */}
        <div className="agent-detail__section">From your Aria app</div>
        {!appCompanyId ? (
          <>
            <p className="sched-muted">Enter your company ID (the <strong>ARIA-XXXX</strong> code from the app) to see jobs and payments your team sends from their phones.</p>
            <input style={inp} type="text" placeholder="ARIA-XXXX" value={appCoInput} onChange={(e) => setAppCoInput(e.target.value)} />
            <button type="button" className="agent-detail__cta" style={{ marginTop: 10 }} onClick={connectApp}>Connect app</button>
          </>
        ) : (
          <>
            {appSummary && (
              <div className="sched-event__m" style={{ marginBottom: 6 }}>
                <strong>{money(appSummary.collectedUsd || 0)}</strong> collected · {money(appSummary.requestedUsd || 0)} awaiting · {appSummary.jobs || 0} job{(appSummary.jobs === 1) ? '' : 's'}
              </div>
            )}
            {appLoading ? (
              <p className="sched-muted">Loading…</p>
            ) : appJobs.length === 0 ? (
              <p className="sched-muted">No app jobs yet for {appCompanyId}.</p>
            ) : (
              <ul className="sched-events">{appJobs.map(appCard)}</ul>
            )}
            <button type="button" className="sched-refresh" onClick={loadApp} disabled={appLoading}>{appLoading ? 'Refreshing…' : 'Refresh app jobs'}</button>
            <button type="button" className="sched-refresh" onClick={disconnectApp} style={{ marginLeft: 8 }}>Change ID</button>
          </>
        )}

        <button type="button" className="sched-refresh" onClick={load} disabled={loading} style={{ marginTop: 12 }}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        <div className="agent-detail__note">Open a job in the Jobs tab to set or edit its price before it goes out.</div>
      </div>
    </div>
  );
}
